import { useEffect, useRef, useCallback } from "react";

// The preview/test host isn't in the production Turnstile widget's allowed domains, so it can't
// mint valid tokens there. On preview we use Cloudflare's official "always passes" TEST site key
// (renders cleanly, auto-verifies); production ships the real site key. The backend mirrors this:
// it skips Turnstile enforcement on the preview host and enforces it in production.
const IS_PREVIEW = (process.env.REACT_APP_BACKEND_URL || "").includes("emergentagent.com");
const TEST_SITE_KEY = "1x00000000000000000000AA";
const SITE_KEY = IS_PREVIEW
  ? TEST_SITE_KEY
  : (process.env.REACT_APP_TURNSTILE_SITE_KEY || TEST_SITE_KEY);

let scriptPromise = null;
function loadTurnstileScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    if (window.turnstile) return resolve();
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export function TurnstileWidget({ onToken, theme = "light", className = "", siteKey }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const emit = useCallback((t) => onToken && onToken(t), [onToken]);
  const key = siteKey || SITE_KEY;

  useEffect(() => {
    let cancelled = false;
    loadTurnstileScript().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: key,
          theme,
          // Telemetry marker for Cloudflare's Spin analytics (account-level aggregate; no
          // per-user data). Removing this doesn't break the integration.
          action: "turnstile-spin-v2",
          callback: (token) => emit(token),
          "expired-callback": () => emit(""),
          "error-callback": () => emit(""),
        });
      } catch (e) {
        /* already-rendered / transient — ignore */
      }
    });
    return () => {
      cancelled = true;
      try {
        if (widgetIdRef.current && window.turnstile) window.turnstile.remove(widgetIdRef.current);
      } catch (e) {
        /* ignore */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, key]);

  return <div ref={containerRef} className={className} data-testid="turnstile-widget" />;
}
